import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  formatDateFilterValue,
  useColumnFilters,
} from '../../components/admin/ColumnFilterHeader';
import { formatCurrency } from './receptionistData';
import { PAYMENTS_UPDATE_EVENT, notifyPaymentsUpdated } from './paymentStore';
import { fetchPaymentsListPage, deletePayment } from '../../services/receptionist/receptionistApi';
import { isReceptionistPanelScope } from '../../services/gym/branchContext';
import { pkTodayRange } from '../../utils/pkTime';
import { useServerPagedList } from '../../hooks/useServerPagedList';
import TablePagination from '../../components/shared/TablePagination';
import PrintActionsHeader from '../../components/shared/PrintActionsHeader';
import { usePanelToast } from '../../context/PanelToastContext';
import {
  exportPaymentCsv,
  exportPaymentExcel,
  exportPaymentPdf,
  exportPayments,
  printPaymentList,
} from './exportPayments';
import {
  exportPaymentReceiptCsv,
  exportPaymentReceiptExcel,
  exportPaymentReceiptPdf,
  printPaymentReceipt,
} from './exportReceipt';
import '../owner/ownerPages.css';
import '../owner/subscriptions/subscriptions.css';
import '../owner/finance/finance.css';
import '../admin/adminListPages.css';
import './members/members.css';
import { dedupePayments } from '../../utils/dedupeList';
import SaleReceiptModal from './sales/SaleReceiptModal';
import PayBalanceModal from './sales/PayBalanceModal';
import SaleEditModal from './sales/SaleEditModal';
import SaleHistoryRowActions from './sales/SaleHistoryRowActions';
import { buildReceiptFromPaymentRow } from './sales/buildSaleReceipt';
import { getSalePageConfig } from './sales/salePageConfig';
import { saleRowEndDate, saleRowStartDate, saleRowDuration, saleRowPackageName } from './sales/salePeriodUtils';
import {
  paymentTotalDue,
  saleRowLeft,
  saleRowPaid,
} from './sales/salePaymentUtils';
import './payments.css';

function isSaleSource(source) {
  const key = String(source || '').toLowerCase();
  return key.endsWith('_sale') || key === 'gym_apparel';
}

function saleMetaForPayment(p) {
  const config = getSalePageConfig(String(p.source || '').replace(/_/g, '-'));
  if (config) return { title: config.title, label: config.label };
  return { title: 'Payment', label: 'Payment' };
}

function Payments() {
  const toast = usePanelToast();
  const receptionistScope = isReceptionistPanelScope();
  const todayRange = pkTodayRange();
  const [search, setSearch] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const listRangeStart = receptionistScope ? todayRange.startDate : rangeStart;
  const listRangeEnd = receptionistScope ? todayRange.endDate : rangeEnd;
  const [saleReceipt, setSaleReceipt] = useState(null);
  const [receiptTarget, setReceiptTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    id: '',
    gymId: '',
    member: '',
    package: '',
    duration: '',
    total: '',
    amount: '',
    pendingAmount: '',
    method: '',
    startDate: '',
    endDate: '',
    date: '',
    status: '',
  });

  const fetchPage = useCallback((params) => fetchPaymentsListPage(params), []);
  const {
    rows: payments,
    total,
    page,
    setPage,
    totalPages,
    from,
    to,
    loading,
    error,
    setError,
    reload,
    nextPage,
    prevPage,
  } = useServerPagedList({
    fetcher: fetchPage,
    search,
    extra: { dateFrom: listRangeStart, dateTo: listRangeEnd },
    filters,
  });

  useEffect(() => {
    const refresh = () => { reload(); };
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
  }, [reload]);

  const pageItems = payments;

  const handleExport = async (exporterFn) => {
    try {
      const result = await fetchPaymentsListPage({
        search,
        filters,
        dateFrom: listRangeStart,
        dateTo: listRangeEnd,
        forExport: true,
      });
      exporterFn(dedupePayments(result.rows || []));
    } catch (err) {
      setError(err.message || 'Export failed');
    }
  };

  const handleShowReceipt = (p) => {
    if (isSaleSource(p.source)) {
      setSaleReceipt(buildReceiptFromPaymentRow(p, saleMetaForPayment(p)));
      return;
    }
    setReceiptTarget(p);
  };

  const handleBalancePaid = async (receiptData) => {
    setSaleReceipt(receiptData);
    await reload();
  };

  const handleDeletePayment = async () => {
    if (!deleteTarget || deleting) return;
    const paymentDbId = deleteTarget.rawId || deleteTarget.id;
    try {
      setDeleting(true);
      await deletePayment(paymentDbId);
      setDeleteTarget(null);
      notifyPaymentsUpdated();
      toast.showDeleted('Payment');
      await reload();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete payment');
    } finally {
      setDeleting(false);
    }
  };

  const handlePrintReceipt = () => {
    if (!receiptTarget) return;
    printPaymentReceipt(receiptTarget);
  };

  return (
    <div>
      <PageHeader
        title="Payments"
        subtitle="All payments — pay balances, view receipts, and track totals"
        variant="banner"
      />

      <PrintActionsHeader
        startDate={listRangeStart}
        endDate={listRangeEnd}
        onStartDateChange={setRangeStart}
        onEndDateChange={setRangeEnd}
        onClearDates={receptionistScope ? undefined : () => { setRangeStart(''); setRangeEnd(''); }}
        datesReadOnly={receptionistScope}
        onPrint={() => handleExport(printPaymentList)}
        onCsv={() => handleExport(exportPaymentCsv)}
        onPdf={() => handleExport(exportPaymentPdf)}
        onExcel={() => handleExport(exportPaymentExcel)}
        onExport={() => handleExport(exportPayments)}
        ariaLabel="Payment print and export"
      >
        <input
          type="search"
          className="owner-form-input"
          style={{ maxWidth: 260 }}
          placeholder="Search payments..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search payments"
        />
      </PrintActionsHeader>
      {receptionistScope ? (
        <p className="owner-placeholder-text owner-mb-16">
          {search.trim()
            ? 'Searching all dates in this branch'
            : 'Pakistan time · today only — payments collected today'}
        </p>
      ) : null}

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading payments...</p>}

      <div className="owner-card rec-panel-card rec-member-list-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="ID"
                  filterKey="id"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Filter ID..."
                />
                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="gymId"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Filter serial no..."
                />
                <ColumnFilterHeader
                  label="Member"
                  filterKey="member"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Filter member..."
                />
                <ColumnFilterHeader
                  label="Package"
                  filterKey="package"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search package..."
                />
                <ColumnFilterHeader
                  label="Duration"
                  filterKey="duration"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search duration..."
                />
                <ColumnFilterHeader
                  label="Total"
                  filterKey="total"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search total..."
                />
                <ColumnFilterHeader
                  label="Total paid"
                  filterKey="amount"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search paid..."
                />
                <ColumnFilterHeader
                  label="Total left"
                  filterKey="pendingAmount"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search left..."
                />
                <ColumnFilterHeader
                  label="Start date"
                  filterKey="startDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="End date"
                  filterKey="endDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Method"
                  filterKey="method"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search method..."
                />
                <ColumnFilterHeader
                  label="Date"
                  filterKey="date"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search status..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length > 0 ? (
                pageItems.map((p) => {
                  const left = saleRowLeft(p);
                  return (
                    <tr key={p.rawId || p.id}>
                      <td>{p.id}</td>
                      <td className="rec-member-gym-id">{p.gymId || '—'}</td>
                      <td>{p.member}</td>
                      <td>{saleRowPackageName(p)}</td>
                      <td>{saleRowDuration(p)}</td>
                      <td>{formatCurrency(paymentTotalDue(p))}</td>
                      <td className="finance-amount-positive">{formatCurrency(saleRowPaid(p))}</td>
                      <td className={left > 0 ? 'finance-amount-negative' : ''}>
                        {formatCurrency(left)}
                      </td>
                      <td>{saleRowStartDate(p)}</td>
                      <td>{saleRowEndDate(p)}</td>
                      <td>{p.method}</td>
                      <td>{p.date}</td>
                      <td>
                        <span className={`owner-badge ${p.status === 'completed' ? 'owner-badge-success' : 'owner-badge-warning'}`}>
                          {p.status === 'completed' ? 'Completed' : 'Pending'}
                        </span>
                      </td>
                      <td>
                        <div className="rec-payment-actions-cell">
                          {p.invoiceId && (
                            <span className="owner-badge owner-badge-info">{p.invoiceId}</span>
                          )}
                          <SaleHistoryRowActions
                            row={p}
                            onReceipt={handleShowReceipt}
                            onPayBalance={left > 0 ? setPayTarget : undefined}
                            onEdit={setEditTarget}
                            onDelete={setDeleteTarget}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={13} className="owner-placeholder-text">
                    No payments found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          from={from}
          to={to}
          onPageChange={setPage}
          onPrev={prevPage}
          onNext={nextPage}
        />
      </div>

      <SaleReceiptModal receipt={saleReceipt} onClose={() => setSaleReceipt(null)} />

      {editTarget && (
        <SaleEditModal
          key={editTarget.rawId || editTarget.id}
          row={editTarget}
          title={`Edit ${saleMetaForPayment(editTarget).label || 'Sale'}`}
          itemLabel="Package / Item"
          itemPlaceholder="Package or item name"
          onClose={() => setEditTarget(null)}
          onSaved={reload}
        />
      )}

      {payTarget && (
        <PayBalanceModal
          key={payTarget.rawId || payTarget.id}
          paymentRow={payTarget}
          saleMeta={saleMetaForPayment(payTarget)}
          onClose={() => setPayTarget(null)}
          onPaid={handleBalancePaid}
        />
      )}

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Payment</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="owner-placeholder-text" style={{ margin: '0 0 16px' }}>
              Delete payment for <strong>{deleteTarget.member || 'this customer'}</strong>? This action cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button type="button" className="owner-btn rec-member-btn-delete" onClick={handleDeletePayment} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {receiptTarget && (
        <div className="subscription-modal-overlay" onClick={() => setReceiptTarget(null)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Payment Receipt</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setReceiptTarget(null)} aria-label="Close">×</button>
            </div>
            <div className="rec-payment-receipt-preview">
              <div className="rec-payment-receipt-row">
                <span>CR. No.</span>
                <strong>{receiptTarget.invoiceId || '—'}</strong>
              </div>
              <div className="rec-payment-receipt-row">
                <span>Payment ID</span>
                <strong>{receiptTarget.id}</strong>
              </div>
              <div className="rec-payment-receipt-row">
                <span>Serial No</span>
                <strong>{receiptTarget.gymId || '—'}</strong>
              </div>
              <div className="rec-payment-receipt-row">
                <span>Member</span>
                <strong>{receiptTarget.member}</strong>
              </div>
              <div className="rec-payment-receipt-row">
                <span>Package</span>
                <strong>{receiptTarget.package}</strong>
              </div>
              <div className="rec-payment-receipt-row">
                <span>Total</span>
                <strong>{formatCurrency(paymentTotalDue(receiptTarget))}</strong>
              </div>
              <div className="rec-payment-receipt-row">
                <span>Payment Method</span>
                <strong>{receiptTarget.method}</strong>
              </div>
              <div className="rec-payment-receipt-row">
                <span>Date</span>
                <strong>{receiptTarget.date}</strong>
              </div>
              <div className="rec-payment-receipt-row rec-payment-receipt-row-highlight">
                <span>Amount Paid</span>
                <strong>{formatCurrency(saleRowPaid(receiptTarget))}</strong>
              </div>
              {saleRowLeft(receiptTarget) > 0 && (
                <div className="rec-payment-receipt-row">
                  <span>Total Left</span>
                  <strong>{formatCurrency(saleRowLeft(receiptTarget))}</strong>
                </div>
              )}
              {receiptTarget.paymentNote && (
                <div className="rec-payment-receipt-row">
                  <span>Payment Note</span>
                  <strong>{receiptTarget.paymentNote}</strong>
                </div>
              )}
            </div>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn owner-btn-primary" onClick={handlePrintReceipt}>
                Print Receipt
              </button>
              <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportPaymentReceiptPdf(receiptTarget)}>
                Export PDF
              </button>
              <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportPaymentReceiptCsv(receiptTarget)}>
                Export CSV
              </button>
              <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportPaymentReceiptExcel(receiptTarget)}>
                Export Excel
              </button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setReceiptTarget(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payments;
